import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { CollisionDetection, DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import DealColumn from "./DealColumn";
import ContractTermsPrompt from "./ContractTermsPrompt";
import { DealCardFace } from "./DealCard";
import { BOARD_COLUMNS, phaseForColumn } from "@/utils/deals.utils";
import { useMoveDealPhase } from "@/hooks/useDeals";
import { BoardColumn, DealPhase } from "@/enum/common.enum";
import type { Deal } from "@/types/deals.types";

type BoardColumnId = (typeof BOARD_COLUMNS)[number]["id"];

interface DealBoardProps {
  grouped: Record<BoardColumnId, Deal[]>;
  /** Internal users can change a deal's phase by dragging its card to another
   *  column — the same move the Advance button makes on the deal detail page,
   *  minus the one-step-forward restriction. */
  canDrag?: boolean;
}

/**
 * Cards are big and columns are wide, so `pointerWithin` — "which column is the
 * cursor actually inside" — is the honest answer nearly always. `rectIntersection`
 * only picks up the edge case where the cursor has left every column but the
 * dragged card still overlaps one; without the fallback that drop is silently
 * discarded.
 */
const collisionDetection: CollisionDetection = (args) => {
  const withinPointer = pointerWithin(args);
  return withinPointer.length > 0 ? withinPointer : rectIntersection(args);
};

export default function DealBoard({ grouped, canDrag = false }: DealBoardProps) {
  const [activeDeal, setActiveDeal] = useState<Deal | null>(null);
  // The deal to offer contract terms for, once it has actually landed under
  // contract. Held separately from `activeDeal`, which is cleared on drop.
  const [termsFor, setTermsFor] = useState<Deal | null>(null);
  const { mutate: moveDealPhase } = useMoveDealPhase();
  const navigate = useNavigate();

  const sensors = useSensors(
    // 8px of travel before a drag begins — under that it's a click, and the
    // card (which is a link) navigates to the deal as it always has.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Press-and-hold on touch, so a finger swipe still scrolls the board.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    // Space, not Enter: Enter belongs to the link underneath.
    useSensor(KeyboardSensor, {
      keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space"] },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDeal((event.active.data.current?.deal as Deal) ?? null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveDeal(null);
    if (!over) return;
    const deal = active.data.current?.deal as Deal | undefined;
    // Droppable ids are BOARD_COLUMNS ids by construction; `phaseForColumn`
    // returning non-null is what actually proves the column exists.
    const columnId = String(over.id) as BoardColumn;
    if (!deal || deal.board_column === columnId) return;
    // Columns are pathway-agnostic; the phase they map to is not. Null means
    // the column is unknown or the deal is already in that phase.
    const phase = phaseForColumn(deal, columnId);
    if (!phase) return;
    moveDealPhase(
      { dealId: deal.id, phase, boardColumn: columnId },
      {
        // Only once the move has actually stuck. Offering the terms optimistically
        // would leave an agent filling in a form for a phase change that failed.
        onSuccess: (res) => {
          if (phase === DealPhase.UNDER_CONTRACT) setTermsFor(res.data ?? deal);
        },
      }
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDeal(null)}
    >
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="deal-board" id="deal-board">
        {BOARD_COLUMNS.map((column) => (
          <DealColumn key={column.id} column={column} deals={grouped[column.id]} draggable={canDrag} />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeDeal && (
          <DealCardFace deal={activeDeal} className="cursor-grabbing border-dash-greenDeep shadow-lg" />
        )}
      </DragOverlay>
      {/* Optional, and strictly after the fact — the deal is already under
          contract whether these get filled in or not. */}
      {termsFor && (
        <ContractTermsPrompt
          deal={termsFor}
          onClose={() => setTermsFor(null)}
          // Saving means the agent is now working this deal — take them into it.
          onSaved={(fresh) => navigate(`/deals/${fresh.id ?? termsFor.id}`)}
        />
      )}
    </DndContext>
  );
}
